import type { ApolloClient } from '@apollo/client';
import { GetFolderAssets } from '../../shared/gql/generated/GetFolderAssets';
import { getFolderAssetsConnection } from '../../shared/utils/getFolderAssetsConnection';

/**
 * Refetches the child folders of a folder from the first page up to a limit.
 * The query used here doesn't provide a sortBy to the GetFolderAssets query and
 * is meant to be used to refetch the folders in the FolderTree.
 *
 * @param client The ApolloClient instance
 * @param accountId The id of the account containing the folder
 * @param folderId The id of the parent folder to refetch
 */
export function refetchFolderChildren(
  client: ApolloClient<any>,
  accountId: string | undefined,
  folderId: string
) {
  if (!accountId) return;

  const cached = client.cache.readQuery({
    query: GetFolderAssets,
    variables: {
      accountId,
      assetType: 'FOLDER',
      folderId,
      page: {
        first: REFETCH_PAGE_SIZE,
      },
    },
  });

  const folderAssets = getFolderAssetsConnection(cached ?? undefined);
  const length = folderAssets?.nodes.length ?? 0;

  if (!folderAssets || length > REFETCH_LIMIT) return;

  const first =
    length === 0
      ? REFETCH_PAGE_SIZE
      : Math.ceil(length / REFETCH_PAGE_SIZE) * REFETCH_PAGE_SIZE;

  client.query({
    fetchPolicy: 'network-only',
    query: GetFolderAssets,
    variables: {
      accountId,
      assetType: 'FOLDER',
      folderId,
      page: {
        first,
      },
    },
  });
}

const REFETCH_PAGE_SIZE = 200;
const REFETCH_LIMIT = 5000;
